import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {TitleBreadcrumbsChangedAction, TitleChangedAction} from '../../../../core/actions/title.actions';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../app/app.state';
import {Subscription} from 'rxjs';
import {App, APP_TYPE_PICTURE, APP_TYPE_PROP_MANAGER, APP_TYPE_TENANT_PORTAL, AppId} from '../../../../shared/models/app';
import {Tile, TileId} from '../../../../shared/models/tile';
import {AppsState} from '../../../../core/state/apps.state';
import {AppsService} from '../../../../core/services/apps.service';
import {SearchService} from '../../../../core/services/search.service';
import {TilesState} from '../../../../core/state/tiles.state';
import {UrlService} from '../../../../core/services/url.service';
import {ImageCardAppComponent} from '../../../components/image-card-app/image-card-app.component';

interface AppGroups {
  picture: App[];
  propManager: App[];
  tenantPortal: App[];
}

@Component({
  selector: 'motn-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ImageCardAppComponent,
  ],
})
export class HomeComponent implements OnInit, OnDestroy {
  private appsSub: Subscription;
  private searchSub: Subscription;
  private tilesSub: Subscription;

  apps: App[] = [];
  appGroups: AppGroups;
  private _apps: App[] = [];
  private _tiles: { [appId: AppId]: { [tileId: TileId]: Tile } } = {};
  private searchTerm: string;


  constructor(
    private store: Store<AppState>,
    private searchService: SearchService,
    private appsService: AppsService,
    private urlService: UrlService,
  ) {
  }


  ngOnInit(): void {
    this.updateTitle();

    this.searchService.enableSearch();

    this.searchSub = this.urlService.searchTerm$.subscribe((term) => {
      this.searchTerm = term;
      this.render();
    });

    this.appsSub = this.store.select('apps').subscribe((appsState: AppsState) => {
      this._apps = [...appsState.apps];
      this.updateTitle();
      this.render();
    });

    this.appsService.fetchUserApps$().subscribe();

    this.tilesSub = this.store.select('tiles').subscribe(
      (data: TilesState) => {
        this._tiles = data.byApp;
        this.render();
      },
    );

    this.render();
  }


  render() {
    let shownApps: App[];

    if (!this.searchTerm) {
      shownApps = this._apps;
    } else {
      // first search apps' tiles to see if search result matches a tile name
      let appsWithTileName = new Set<AppId>();

      for (let appId in this._tiles) {
        for (let tileId in this._tiles[appId]) {
          if (this._tiles[appId][tileId].Name.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1) {
            appsWithTileName.add(appId);
          }
        }
      }

      // now combine the first search with search matches with app names and subtitles
      shownApps = this._apps.filter((app) => {
        const matchesAppName = app.Name.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1;
        const matchesSubtitle = app.Config?.Subtitle?.Text?.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1;
        return appsWithTileName.has(app.ID) || matchesAppName || matchesSubtitle;
      });
    }

    shownApps = shownApps.sort((a, b): number => {
      let nameA = a.Name;
      let nameB = b.Name;

      if (a.Config?.Subtitle?.Text) {
        nameA += a.Config.Subtitle.Text;
      }
      if (b.Config?.Subtitle?.Text) {
        nameB += b.Config.Subtitle.Text;
      }

      if (nameA < nameB) {
        return -1;
      }

      return 1;
    });

    this.searchService.setResultQty(shownApps.length);
    this.apps = [...shownApps];

    this.makeAppGroups();
  }


  private makeAppGroups(): AppGroups {
    const appGroups: AppGroups = {
      picture: [],
      propManager: [],
      tenantPortal: [],
    };

    for (let app of this.apps) {
      switch (app.Type) {
        case APP_TYPE_PICTURE:
          appGroups.picture.push(app);
          break;
        case APP_TYPE_PROP_MANAGER:
          appGroups.propManager.push(app);
          break;
        case APP_TYPE_TENANT_PORTAL:
          appGroups.tenantPortal.push(app);
          break;
        default:
          throw new Error(`unknown app type ${app.Type}`);
      }
    }

    this.appGroups = appGroups;
    return appGroups;
  }


  ngOnDestroy() {
    this.searchSub?.unsubscribe();
    this.appsSub?.unsubscribe();
    this.tilesSub?.unsubscribe();
  }


  trackBy(index, app: App) {
    return app?.ID;
  }


  updateTitle() {
    let qty: number = 0;
    if (this._apps.length) {
      qty = this._apps.length;
    }

    let title = 'Apps';
    if (qty > 1) {
      title = `${qty} Apps`;
    }

    this.store.dispatch(new TitleChangedAction(
      '',
      title,
    ));

    this.store.dispatch(new TitleBreadcrumbsChangedAction([
      {
        text: 'Apps',
      },
      {
        text: 'Home',
      },
    ]));
  }
}
